/**
 * "Kom igång" (slice C, P1-11; persona report §4): four light questions on Home for a student who
 * has not answered them yet — where they are in the course (with the lectures they missed), the
 * grade they aim for, the time they have per day and how sure they feel about the maths — plus a
 * short "Ord du kommer att se" list and a link to the help page.
 *
 * The answers are stored on the account (`PATCH /api/auth/me { prefs }`), so the card does not
 * come back on another device. "Hoppa över" stores the preselected defaults with outcome
 * "skipped", which also hides the card for good. `/home?kom-igang=1` (Settings → "Ändra mina
 * val", and the help page) opens it again with the stored answers; there "Avbryt" just closes it.
 *
 * Teachers do not get the card unasked (they can open it from Settings), and it is hidden while an
 * exam runs. Teachers see the answers only as aggregate counts (ruling 8).
 */
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  COURSE_POSITIONS,
  MATH_CONFIDENCE,
  MINUTES_PER_DAY,
  STUDY_GOALS,
  prefsInputOf,
  type CoursePosition,
  type MathConfidence,
  type OnboardingOutcome,
  type StudyGoal,
  type UserPrefsInput,
} from "@shared/userPrefs";
import { useAuth } from "@client/app/auth";
import { useExamMode } from "@client/app/examMode";
import { Button, Card, Checkbox, RadioGroup, useToast } from "@client/components/ui";
import { useLectureTimeline } from "@client/features/content/course";
import { useLang, useT, type CoreKey } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { formatDayShort } from "@client/lib/format";

/** The query flag that opens the card again for a student who already answered it. */
export const REOPEN_PARAM = "kom-igang";
export const REOPEN_HREF = `/home?${REOPEN_PARAM}=1`;

const GLOSSARY: readonly CoreKey[] = [
  "onboarding.glossary.moment",
  "onboarding.glossary.lectures",
  "onboarding.glossary.today",
  "onboarding.glossary.types",
  "onboarding.glossary.confidence",
  "onboarding.glossary.principle",
];

const CHIP_GROUP = "flex-row flex-wrap gap-x-5 gap-y-2";

/** Whether Home shows the card: a student with no stored answers, or the reopen flag. */
export function useKomIgangVisible(): boolean {
  const { user } = useAuth();
  const { examActive } = useExamMode();
  const [params] = useSearchParams();
  if (!user || examActive) return false;
  if (params.get(REOPEN_PARAM) === "1") return true;
  return user.prefs === null && user.role === "student";
}

export function KomIgangCard() {
  const visible = useKomIgangVisible();
  if (!visible) return null;
  return <KomIgangForm />;
}

function KomIgangForm() {
  const t = useT();
  const { lang } = useLang();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, updateMe } = useAuth();
  const { timeline } = useLectureTimeline();
  const reopened = user?.prefs != null;
  const [answers, setAnswers] = useState<UserPrefsInput>(() => prefsInputOf(user?.prefs));
  const [saving, setSaving] = useState<OnboardingOutcome | null>(null);

  // Lectures one can have missed: those already held, today's included.
  const held = timeline ? [...timeline.done, ...(timeline.today ? [timeline.today] : [])] : [];
  const lectureLabel = (lecture: { id: string; date: string }) =>
    `${lecture.id} · ${formatDayShort(lecture.date, lang)}`;
  const focus = timeline?.focus;
  const previous = timeline?.previous;
  const whereHint = focus
    ? t("onboarding.position.next", { lecture: lectureLabel(focus) })
    : previous
      ? t("onboarding.position.last", { lecture: lectureLabel(previous) })
      : undefined;

  function set<K extends keyof UserPrefsInput>(key: K, value: UserPrefsInput[K]) {
    setAnswers((current) => ({ ...current, [key]: value }));
  }

  function toggleMissed(id: string, checked: boolean) {
    setAnswers((current) => ({
      ...current,
      missed: checked
        ? [...current.missed.filter((m) => m !== id), id]
        : current.missed.filter((m) => m !== id),
    }));
  }

  function close() {
    navigate("/home", { replace: true });
  }

  async function submit(outcome: OnboardingOutcome) {
    const prefs: UserPrefsInput =
      outcome === "skipped"
        ? { ...prefsInputOf(null), outcome }
        : {
            ...answers,
            missed: answers.position === "missed" ? answers.missed : [],
            outcome,
          };
    setSaving(outcome);
    try {
      await updateMe({ prefs });
      // Today's defaults (cards, new problems, catch-up order) follow the answers.
      await queryClient.invalidateQueries({ queryKey: ["practice", "today"] });
      toast.success(t(outcome === "skipped" ? "onboarding.skipped" : "onboarding.saved"));
      close();
    } catch (error) {
      toast.error(messageForError(error, t));
    } finally {
      setSaving(null);
    }
  }

  return (
    <Card
      className="mb-6"
      aria-labelledby="kom-igang-title"
      data-testid="kom-igang"
      title={<span id="kom-igang-title">{t("onboarding.title")}</span>}
      description={t("onboarding.intro")}
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          void submit("saved");
        }}
      >
        <div>
          <RadioGroup<CoursePosition>
            name="kom-igang-position"
            legend={t("onboarding.position.legend")}
            value={answers.position}
            className={CHIP_GROUP}
            options={COURSE_POSITIONS.map((value) => ({
              value,
              label: t(`onboarding.position.${value}`),
            }))}
            onChange={(value) => set("position", value)}
          />
          {whereHint && <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">{whereHint}</p>}
          {answers.position === "missed" && (
            <fieldset className="mt-3">
              <legend className="mb-1 text-sm text-ink-700 dark:text-ink-200">
                {t("onboarding.missed.legend")}
              </legend>
              {held.length ? (
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {held.map((lecture) => (
                    <Checkbox
                      key={lecture.id}
                      label={lectureLabel(lecture)}
                      checked={answers.missed.includes(lecture.id)}
                      onChange={(event) => toggleMissed(lecture.id, event.target.checked)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-xs text-ink-500 dark:text-ink-400">
                  {t("onboarding.missed.none")}
                </p>
              )}
            </fieldset>
          )}
        </div>

        <RadioGroup<StudyGoal>
          name="kom-igang-goal"
          legend={t("onboarding.goal.legend")}
          value={answers.goal}
          className={CHIP_GROUP}
          options={STUDY_GOALS.map((value) => ({ value, label: t(`onboarding.goal.${value}`) }))}
          onChange={(value) => set("goal", value)}
        />

        <RadioGroup<string>
          name="kom-igang-minutes"
          legend={t("onboarding.minutes.legend")}
          value={String(answers.minutesPerDay)}
          className={CHIP_GROUP}
          options={MINUTES_PER_DAY.map((value) => ({
            value: String(value),
            label: t(`onboarding.minutes.${value}`),
          }))}
          onChange={(value) => {
            const minutes = MINUTES_PER_DAY.find((m) => String(m) === value);
            if (minutes) set("minutesPerDay", minutes);
          }}
        />

        <RadioGroup<MathConfidence>
          name="kom-igang-math"
          legend={t("onboarding.math.legend")}
          value={answers.mathConfidence}
          className={CHIP_GROUP}
          options={MATH_CONFIDENCE.map((value) => ({
            value,
            label: t(`onboarding.math.${value}`),
          }))}
          onChange={(value) => set("mathConfidence", value)}
        />

        <details className="text-sm">
          <summary className="cursor-pointer font-medium text-ink-800 dark:text-ink-100">
            {t("onboarding.glossary.title")}
          </summary>
          <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-ink-700 dark:text-ink-300">
            {GLOSSARY.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
          <p className="mt-2 text-ink-600 dark:text-ink-400">
            {t("onboarding.glossary.language")}{" "}
            <Link
              to="/hjalp"
              className="text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
            >
              {t("onboarding.glossary.more")}
            </Link>
          </p>
        </details>

        <p className="text-xs text-ink-500 dark:text-ink-400">{t("onboarding.privacy")}</p>

        <div className="flex flex-wrap gap-3">
          <Button type="submit" loading={saving === "saved"} disabled={saving !== null}>
            {t("onboarding.save")}
          </Button>
          {reopened ? (
            <Button type="button" variant="secondary" onClick={close} disabled={saving !== null}>
              {t("common.cancel")}
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              loading={saving === "skipped"}
              disabled={saving !== null}
              onClick={() => void submit("skipped")}
            >
              {t("onboarding.skip")}
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
}
